import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, MenuItem,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import * as clienteService from "../services/clienteService";
import TruncatedCellText from "./TruncatedCellText";

const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[900]
    : theme.palette.grey[50],
  color: theme.palette.text.secondary,
}));

const DIALOG_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const DEPARTAMENTOS = [
  "La Paz", "Cochabamba", "Santa Cruz", "Oruro",
  "Potosi", "Chuquisaca", "Tarija", "Beni", "Pando",
];

const TIPOS_DOCUMENTO_IDENTIDAD = [
  { value: 1, label: "CEDULA DE IDENTIDAD" },
  { value: 2, label: "CEDULA DE IDENTIDAD DE EXTRANJERO" },
  { value: 3, label: "PASAPORTE" },
  { value: 4, label: "OTRO DOCUMENTO DE IDENTIDAD" },
  { value: 5, label: "NIT" },
];

const LEGAL_INI = {
  codigo_documento_identidad: 5,
  nit: "",
  nombre_facturar: "",
  correo: "",
  domicilio_legal: "",
  ciudad: "",
  observacion: "",
  estado: "Activo",
};

const InformacionFacturacionDialog = ({ open, cliente, servicio, onClose, initialData }) => {
  const [legales, setLegales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(LEGAL_INI);

  const cargarLegales = useCallback(async () => {
    if (!cliente?.cod_cliente || !servicio?.cod_servicio_cliente) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await clienteService.getLegalServicio(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
      );
      setLegales(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo cargar la informacion de facturacion");
      setLegales([]);
    } finally {
      setLoading(false);
    }
  }, [cliente?.cod_cliente, servicio?.cod_servicio_cliente]);

  useEffect(() => {
    if (open) {
      setEditing(null);
      // Si se abre para avisar que falta el registro de este servicio, se precarga con
      // lo que ya se estaba mostrando por defecto (última factura / informacion_legal
      // del cliente), en vez de un formulario vacío — solo falta confirmar/guardar.
      setForm(initialData ? { ...LEGAL_INI, ...initialData } : LEGAL_INI);
      setFormError(null);
      cargarLegales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cargarLegales]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNew = () => {
    setEditing(null);
    setForm(LEGAL_INI);
    setFormError(null);
  };

  const handleEdit = (legal) => {
    setEditing(legal);
    setForm({
      codigo_documento_identidad: Number(legal.codigo_documento_identidad) || 5,
      nit: legal.nit || "",
      nombre_facturar: legal.nombre_facturar || "",
      correo: legal.correo || "",
      domicilio_legal: legal.domicilio_legal || "",
      ciudad: legal.ciudad || "",
      observacion: legal.observacion || "",
      estado: legal.estado || "Activo",
    });
    setFormError(null);
  };

  const handleSaveLegal = async () => {
    if (!form.nit.trim()) {
      setFormError("El NIT es requerido");
      return;
    }
    if (!form.nombre_facturar.trim()) {
      setFormError("El nombre a facturar es requerido");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        codigo_documento_identidad: Number(form.codigo_documento_identidad) || 5,
        nit: form.nit.trim(),
        nombre_facturar: form.nombre_facturar.trim(),
        correo: form.correo.trim(),
        domicilio_legal: form.domicilio_legal.trim(),
        ciudad: form.ciudad,
        observacion: form.observacion.trim(),
        estado: form.estado === "Activo" ? "Activo" : "Inactivo",
      };

      if (editing?.id_legal) {
        await clienteService.updateLegalServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
          editing.id_legal,
          payload,
        );
      } else {
        await clienteService.createLegalServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
          payload,
        );
      }

      await cargarLegales();
      handleNew();
    } catch (err) {
      setFormError(err?.response?.data?.message || "No se pudo guardar la informacion de facturacion");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Informacion de facturacion - Servicio #{servicio.cod_servicio_cliente}</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ ...DIALOG_COMPACT_SX, mb: 1.5 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField select fullWidth size="small" label="Tipo documento"
                name="codigo_documento_identidad"
                value={form.codigo_documento_identidad}
                onChange={handleFormChange}
                disabled={saving}>
                {TIPOS_DOCUMENTO_IDENTIDAD.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth required size="small" label="NIT" name="nit"
                value={form.nit} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Nombre a facturar" name="nombre_facturar"
                value={form.nombre_facturar} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Correo electronico" name="correo" type="email"
                value={form.correo} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Domicilio legal" name="domicilio_legal"
                value={form.domicilio_legal} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select fullWidth size="small" label="Ciudad" name="ciudad"
                value={form.ciudad} onChange={handleFormChange} disabled={saving}>
                <MenuItem value="">Seleccionar...</MenuItem>
                {DEPARTAMENTOS.map((departamento) => (
                  <MenuItem key={departamento} value={departamento}>{departamento}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField fullWidth size="small" label="Observacion" name="observacion"
                value={form.observacion} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.estado === "Activo"}
                    disabled={saving}
                    onChange={(e) => setForm((p) => ({ ...p, estado: e.target.checked ? "Activo" : "Inactivo" }))}
                  />
                )}
                label="Activo"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button variant="outlined" size="small" onClick={handleNew} disabled={saving}
                startIcon={<AddIcon />} sx={{ minHeight: 32 }}>
                Nuevo
              </Button>
              <Button variant="contained" size="small" onClick={handleSaveLegal} disabled={saving}
                startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ minHeight: 32 }}>
                {editing ? "Actualizar" : "Guardar"}
              </Button>
            </Grid>
          </Grid>
          {formError && <Alert severity="error" sx={{ mt: 1.5 }}>{formError}</Alert>}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Cargando informacion...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !legales.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Sin informacion de facturacion registrada para este servicio
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SubHeaderCell>NIT</SubHeaderCell>
                  <SubHeaderCell>Nombre a facturar</SubHeaderCell>
                  <SubHeaderCell>Correo</SubHeaderCell>
                  <SubHeaderCell>Domicilio</SubHeaderCell>
                  <SubHeaderCell>Ciudad</SubHeaderCell>
                  <SubHeaderCell>Observacion</SubHeaderCell>
                  <SubHeaderCell align="center">Estado</SubHeaderCell>
                  <SubHeaderCell align="center">Accion</SubHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {legales.map((legal) => (
                  <TableRow key={legal.id_legal} hover sx={legal.estado === "Inactivo" ? { opacity: 0.55 } : undefined}>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <Chip label={legal.nit || "—"} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={legal.nombre_facturar} maxWidth={220} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={legal.correo} maxWidth={180} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={legal.domicilio_legal} maxWidth={190} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{legal.ciudad || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={legal.observacion} maxWidth={220} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={legal.estado === "Inactivo" ? "Inactivo" : "Activo"}
                        size="small"
                        color={legal.estado === "Inactivo" ? "default" : "success"}
                        sx={{ height: 20, fontSize: "0.68rem" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar informacion de facturacion">
                        <IconButton size="small" onClick={() => handleEdit(legal)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InformacionFacturacionDialog;
